import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";

const SidePanelContext = createContext();

// Default and boundary values for panel width
const DEFAULT_WIDTH_PERCENT = 35;
const MIN_WIDTH_PERCENT = 25;
const MAX_WIDTH_PERCENT = 80;

// Local storage key for saving panel width preference
const PANEL_WIDTH_STORAGE_KEY = "quizmate_panel_width";

export const SidePanelProvider = ({ children }) => {
  const [panelFile, setPanelFile] = useState(null);
  const [panelTitle, setPanelTitle] = useState("");
  const [contentComponent, setContentComponent] = useState(null);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [position, setPosition] = useState("right");
  const [documentTabs, setDocumentTabs] = useState([]);
  const [activeTabId, setActiveTabId] = useState(null);

  // Initialize width from localStorage or use default
  const [width, setWidth] = useState(() => {
    const savedWidth = localStorage.getItem(PANEL_WIDTH_STORAGE_KEY);
    return savedWidth ? parseInt(savedWidth, 10) : DEFAULT_WIDTH_PERCENT;
  });

  // Save width to localStorage when it changes
  useEffect(() => {
    localStorage.setItem(PANEL_WIDTH_STORAGE_KEY, width.toString());
  }, [width]);

  // Open panel to view a File (PDF/DOCX)
  const openFilePanel = useCallback((file) => {
    if (!file) return;

    // Use functional update to avoid stale closure over documentTabs
    setDocumentTabs((prev) => {
      const existingTab = prev.find((tab) => tab.name === file.name);
      if (existingTab) {
        setActiveTabId(existingTab.id);
        setPanelFile(existingTab.file);
        setPanelTitle(existingTab.name);
        return prev;
      }
      const tabId = file.name + "_" + Date.now();
      const newTab = { id: tabId, name: file.name, file, type: "document" };
      setActiveTabId(tabId);
      setPanelFile(file);
      setPanelTitle(file.name);
      return [...prev, newTab];
    });

    setContentComponent(null);
    setIsCollapsed(false);

    // Auto-close sidebar when opening a document
    // Dispatch custom event to notify HomePage to close sidebar
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent("closeSidebar"));
    }, 100);
  }, []);

  // Switch to a specific tab
  const switchToTab = useCallback(
    (tabId) => {
      const tab = documentTabs.find((t) => t.id === tabId);
      if (tab) {
        setActiveTabId(tabId);
        if (tab.type === "component") {
          setPanelFile(null);
          setPanelTitle(tab.name);
          setContentComponent(tab.component || null);
        } else {
          setPanelFile(tab.file);
          setPanelTitle(tab.name);
          setContentComponent(null);
        }
      }
    },
    [documentTabs],
  );

  // Close a specific tab
  const closeTab = useCallback(
    (tabId) => {
      const updatedTabs = documentTabs.filter((tab) => tab.id !== tabId);
      setDocumentTabs(updatedTabs);

      if (activeTabId === tabId) {
        if (updatedTabs.length > 0) {
          // Switch to the last remaining tab
          const lastTab = updatedTabs[updatedTabs.length - 1];
          setActiveTabId(lastTab.id);
          if (lastTab.type === "component") {
            setPanelFile(null);
            setPanelTitle(lastTab.name);
            setContentComponent(lastTab.component || null);
          } else {
            setPanelFile(lastTab.file);
            setPanelTitle(lastTab.name);
            setContentComponent(null);
          }
        } else {
          // No tabs left, close panel
          setActiveTabId(null);
          setPanelFile(null);
          setPanelTitle("");
          setIsCollapsed(true);
        }
      }
    },
    [activeTabId, documentTabs],
  );

  // Open panel to render a React component (e.g. Resources list)
  const openComponentPanel = useCallback((component, title = "") => {
    setContentComponent(component);
    setPanelFile(null);
    setPanelTitle(title);
    setIsCollapsed(false);
  }, []);

  // Open a React component as a tab in the panel
  const openComponentTab = useCallback((component, title = "Untitled") => {
    if (!component) return;
    const tabName = title || "Untitled";

    // Use functional updates to avoid race conditions when called rapidly
    setDocumentTabs((prev) => {
      const idx = prev.findIndex(
        (tab) => tab.type === "component" && tab.name === tabName,
      );
      if (idx !== -1) {
        const updated = [...prev];
        const existing = updated[idx];
        // Update component and keep same id
        updated[idx] = { ...existing, component };
        // Activate and update panel states to reflect latest
        setActiveTabId(existing.id);
        setPanelFile(null);
        setPanelTitle(existing.name);
        setContentComponent(component);
        setIsCollapsed(false);
        return updated;
      }
      // Create new tab
      const tabId = tabName + "_" + Date.now();
      const newTab = { id: tabId, name: tabName, type: "component", component };
      // Activate new tab
      setActiveTabId(tabId);
      setPanelFile(null);
      setPanelTitle(tabName);
      setContentComponent(component);
      setIsCollapsed(false);
      return [...prev, newTab];
    });
  }, []);

  const closePanel = useCallback(() => {
    setPanelFile(null);
    setContentComponent(null);
    setDocumentTabs([]);
    setActiveTabId(null);
    setPanelTitle("");
  }, []);

  const closeCurrentContent = useCallback(() => {
    if (activeTabId) {
      closeTab(activeTabId);
    } else {
      setPanelFile(null);
      setContentComponent(null);
      setPanelTitle("");
    }
  }, [activeTabId, closeTab]);

  const toggleCollapse = useCallback(() => {
    setIsCollapsed((prev) => !prev);
  }, []);

  const togglePosition = useCallback(() => {
    setPosition((prev) => (prev === "right" ? "left" : "right"));
  }, []);

  const resizePanel = useCallback((newWidthPercent) => {
    // Ensure width stays within boundaries
    const clampedWidth = Math.max(
      MIN_WIDTH_PERCENT,
      Math.min(MAX_WIDTH_PERCENT, newWidthPercent),
    );
    setWidth(clampedWidth);
  }, []);

  const value = {
    panelFile,
    panelTitle,
    contentComponent,
    isCollapsed,
    position,
    width,
    documentTabs,
    activeTabId,
    openFilePanel,
    openComponentPanel,
    openComponentTab,
    closePanel,
    closeCurrentContent,
    toggleCollapse,
    switchToTab,
    closeTab,
    togglePosition,
    resizePanel,
    setPosition,
    setWidth,
    MIN_WIDTH_PERCENT,
    MAX_WIDTH_PERCENT,
  };

  return (
    <SidePanelContext.Provider value={value}>
      {children}
    </SidePanelContext.Provider>
  );
};

export const useSidePanel = () => {
  const context = useContext(SidePanelContext);
  if (!context) {
    throw new Error("useSidePanel must be used within a SidePanelProvider");
  }
  return context;
};
